import React, { useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { StripeBankAccount, useVerifyBankAccount } from "@ncs/ncs-api"
import { ErrorText, Modal, NumericInputFormField, Paragraph } from "@ncs/web-legos"

export interface VerifyBankAccountModalProps {
	isOpen: boolean
	onClose: () => void
	bankAccount: StripeBankAccount | null
	setSucessfullVerification?: () => void
}

const AccountVerificationFormSchema = z.object({
	amount1: z.number().min(1),
	amount2: z.number().min(1),
})
type AccountVerificationForm = z.infer<typeof AccountVerificationFormSchema>

export const VerifyBankAccountModal: React.FC<VerifyBankAccountModalProps> = ({
	isOpen,
	onClose,
	bankAccount,
	setSucessfullVerification,
}) => {
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [verificationComplete, setVerificationComplete] = useState(false)
	const verifyAccount = useVerifyBankAccount(bankAccount?.id)

	const { control, reset, handleSubmit } = useForm<AccountVerificationForm>({
		resolver: zodResolver(AccountVerificationFormSchema),
	})

	const onSubmit = async (data: AccountVerificationForm) => {
		try {
			setIsSubmitting(true)
			await verifyAccount({
				amount1: data.amount1,
				amount2: data.amount2,
			})
			setVerificationComplete(true)
			setSucessfullVerification?.()
			onClose()
			setErrorMessage("")
		} catch (e) {
			setErrorMessage("Verification unsuccessful")
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleClose = () => {
		reset()
		setErrorMessage(null)
		setIsSubmitting(false)
		setVerificationComplete(false)
		onClose()
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title="Verify Bank Account"
			rightButtons={
				!verificationComplete ?
					{
						buttonText: "Verify",
						variant: "primary-cta",
						onClick: handleSubmit(onSubmit),
						isLoading: isSubmitting,
					}
				:	undefined
			}
		>
			<>
				<Paragraph mb={1}>
					Enter the exact deposit amounts you received into your account, without
					changing or rounding them.
				</Paragraph>
				<Paragraph mb={1}>
					Please input them in the same order they appeared in your account:
				</Paragraph>
				<div>
					<NumericInputFormField
						control={control}
						name="amount1"
						label="Deposit 1"
						fillContainer={false}
					/>
				</div>
				<div>
					<NumericInputFormField
						control={control}
						name="amount2"
						label="Deposit 2"
						fillContainer={false}
					/>
				</div>
			</>
			{!!errorMessage && <ErrorText textAlign="right">{errorMessage}</ErrorText>}
		</Modal>
	)
}
